import { useEffect, useState } from "react";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import ExportToolbar from "@/components/ExportToolbar";
import DateRangePicker from "@/components/DateRangePicker";
import { useReportRange, formatRangeLabel } from "@/lib/useReportRange";
import { exportExcel, exportPDF } from "@/lib/exporters";

function Inner() {
  const { activeId, active } = useCompany();
  // Day Book defaults to today (single day)
  const today = new Date().toISOString().slice(0, 10);
  const { range, setRange, applied, apply, params } = useReportRange({ start: today, end: today });
  const dirty = range.start !== applied.start || range.end !== applied.end;
  const [data, setData] = useState([]);

  useEffect(() => {
    if (!activeId) return;
    api.get(`/companies/${activeId}/reports/day-book`, { params }).then((r) => setData(r.data));
  }, [activeId, applied.start, applied.end, params]);

  const onExcel = () => exportExcel(`day-book-${applied.start}-to-${applied.end}`, [{
    name: "Day Book",
    rows: [
      [`Period: ${formatRangeLabel(range)}`], [],
      ["Date", "Voucher", "Type", "Particulars", "Amount"],
      ...data.map((v) => [v.date, v.voucher_no, v.voucher_type, v.party_ledger_name || v.narration || "", v.total]),
    ],
  }]);

  const onPDF = () => exportPDF(`day-book-${applied.start}-to-${applied.end}`, "Day Book", [{
    title: `Period: ${formatRangeLabel(range)}`,
    headers: ["Date", "Voucher", "Type", "Particulars", "Amount (₹)"],
    rows: data.map((v) => [v.date, v.voucher_no, v.voucher_type, v.party_ledger_name || v.narration || "", formatINR(v.total)]),
  }], { company: active?.name });

  return (
    <>
      <PageHeader
        title="Day Book"
        subtitle={`${active?.name || ""}  •  ${formatRangeLabel(range)}`}
        actions={<ExportToolbar onExcel={onExcel} onPDF={onPDF} onPrint={() => window.print()} />}
      />
      <div className="p-6 space-y-4">
        <DateRangePicker start={range.start} end={range.end} onChange={setRange} onApply={apply} dirty={dirty} />
        <div className="border border-gray-200 rounded bg-white overflow-hidden">
          <table className="tx-table" data-testid="table-day-book">
            <thead>
              <tr><th>Date</th><th>Voucher</th><th>Type</th><th>Party / Narration</th><th className="text-right">Amount</th></tr>
            </thead>
            <tbody>
              {data.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-gray-400">No entries</td></tr>}
              {data.map((v) => (
                <tr key={v.id}>
                  <td className="font-mono text-xs">{v.date}</td>
                  <td className="font-mono">{v.voucher_no}</td>
                  <td><span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{v.voucher_type}</span></td>
                  <td>{v.party_ledger_name || v.narration || "—"}</td>
                  <td className="tx-num">{formatINR(v.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
export default function DayBook() { return <RequireCompany><Inner /></RequireCompany>; }
